/* eslint-disable max-lines */
import styled from 'styled-components';
import { rgba, size } from 'polished';
import { Marker, NavigationControl } from 'react-map-gl';
import { AbstractButton } from '../../atoms/AbstractButton/AbstractButton';
import { MediaQuery } from '../../../styles/mediaQuery';
import { respondTo } from '../../../styles/helpers/respondTo';
import { pseudo } from '../../../styles/helpers/pseudo';
import { hover } from '../../../styles/helpers/hover';
import { Ease } from '../../../styles/easing';
import { FontSize, FontWeight, TransitionDuration } from '../../../styles/variables';
import { typeStyle } from '../../../styles/typeStyle';
import { zIndex } from '../../../styles/zIndex';

export const StyledMapContainer = styled.div`
  ${size('55vh', '100%')};
  position: relative;

  @media ${respondTo(MediaQuery.Small)} {
    height: 65vh;
  }

  @media ${respondTo(MediaQuery.Large)} {
    height: 100vh;
  }
`;

export const StyledMarker = styled(Marker)<{
  $isActive?: boolean;
  $isPopulair?: boolean;
  $isCluster?: boolean;
}>`
  ${size('0')};

  ${({ $isPopulair }) =>
    $isPopulair &&
    `
    z-index: ${zIndex('default')};
  `}

  ${({ $isCluster }) =>
    $isCluster &&
    `
    z-index: ${zIndex('clustered-marker')};
  `}

  ${({ $isActive }) =>
    $isActive &&
    `
    z-index: ${zIndex('active-marker')};
  `}

  ${hover(`
   z-index: ${zIndex('clustered-marker')};
   @media ${respondTo(MediaQuery.Medium)} {
    z-index: ${zIndex('map-overlay')};
  }
  `)};
`;

type StyledAirportNameProps = {
  $isActive?: boolean;
  $isSuspended?: boolean;
  $isInSeasonalService?: boolean;
  $isConnected?: boolean;
  $isCluster?: boolean;
  $pointCount?: number;
  $isUnserved?: boolean;
  $nonStopOnly?: boolean;
};

// location-label
export const StyledLocationName = styled(AbstractButton)<StyledAirportNameProps>`
  ${typeStyle.normal};
  position: absolute;
  top: -0.9375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.875rem;
  padding: 0 0.4375rem;
  background-color: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.black};
  transform: translate(-50%, -100%);
  white-space: nowrap;
  box-shadow: 0 0.25rem 1.25rem rgba(48, 76, 178, 0.2);

  cursor: pointer;
  transition: transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};

  span {
    margin-left: 0.625rem;
  }

  &::before {
    ${pseudo()};
    ${size('0.4375rem')};
    bottom: -0.1875rem;
    border-radius: ${({ theme }) => theme.defaultCssProps.borderRadius.xSmall};
    background-color: ${({ theme }) => theme.color.white};
    transform: rotate(45deg);
  }

  &::after {
    ${pseudo()};
    ${size('0.625rem')};
    bottom: -1.5625rem;
    left: 50%;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.color.blue};
    transform: translate(-50%, -50%);
    transition: transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};
  }

  &.marker-is-disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.color.grey};

    &::after {
      border: transparent;
      background-color: ${({ theme, $isSuspended }) =>
        $isSuspended ? theme.color.orange : theme.color.lighterGrey};
    }

    ${hover(`
    transform: translate(-50%, -100%);

    &::after, svg {
      transform: translate(-50%, -50%);
    }
  `)}
  }

  ${({ $isActive, theme }) =>
    $isActive &&
    `
    top: -0.9375rem;
    background-color: ${theme.color.blue};
    color: ${theme.color.white};

    &::before {
      background-color: ${theme.color.blue};
    }

    &::after {
      height: 0.9375rem;
      width: 0.9375rem;
      bottom: -1.875rem;
    }
    `}

  ${({ $isConnected, theme }) =>
    $isConnected &&
    `
    &::after {
      border: 2px solid ${theme.color.blue};
      background-color: transparent;
    }
  `}

  ${({ $isSuspended, theme }) =>
    $isSuspended &&
    `
    cursor: not-allowed;

    &::after {
      height: 0.5625rem;
      width: 0.5625rem;
      transform: translate(-50%, -50%) rotate(45deg);
      border-radius: ${theme.defaultCssProps.borderRadius.xSmall};
      background-color: ${theme.color.orange};
    }
  `}

  ${({ $isInSeasonalService, theme }) =>
    $isInSeasonalService &&
    `
    svg {
      height: 1.25rem;
      width: 1.25rem;
      color: ${theme.color.darkerGrey};
      position: absolute;
      bottom: -2.25rem;
      left: 50%;
      transform: translate(-50%, -50%);
      transition: transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};
    }

    &::after {
      content: none;
    }
  `}

  ${({ $isSuspended, $isInSeasonalService }) =>
    !$isSuspended &&
    !$isInSeasonalService &&
    `
    ${hover(`
    transform: translate(-50%, calc(-100% + -0.3125rem));

    &::after, svg {
      transform: translate(-50%, calc(-50% + 0.3125rem)) scale(1.1);
    }
  `)}
  `}

  ${({ $isActive }) =>
    $isActive &&
    `
    // transform: none;

    ${hover(`
      // transform: translate(0,calc(-0.3125rem));
      // transform: none;
    `)}
  `}


 ${({ $isCluster, theme }) =>
    $isCluster &&
    `
    margin-top: 1.875rem;
    background-color: ${theme.color.clusterBlue};
    border-radius: 50%;

     &::before {
       display: none;
     }

     &::after {
       display: none;
     }
  `}

   ${({ $isCluster, $isConnected, theme }) =>
    $isCluster &&
    $isConnected &&
    `
      border: 2px solid ${theme.color.blue};
      background-color: ${theme.color.white};
  `}

  ${({ $isCluster, $isUnserved, $isConnected, theme }) =>
    $isCluster &&
    $isUnserved &&
    !$isConnected &&
    `
    cursor: not-allowed;
    background-color: ${theme.color.lighterGrey};
    border: transparent;

     &::before {
      background-color: ${theme.color.lighterGrey};
    }
    &::after {
      border: transparent;
      background-color: ${theme.color.lighterGrey};
    }

    ${hover(`
    transform: translate(-50%, -100%);

    &::after, svg {
      transform: translate(-50%, -50%);
    }
  `)}
  `}

  ${({ $isCluster, $nonStopOnly, $isConnected, theme }) =>
    $isCluster &&
    $nonStopOnly &&
    $isConnected &&
    `
    cursor: not-allowed;
    background-color: ${theme.color.lighterGrey};
    border: transparent;

     &::before {
      background-color: ${theme.color.lighterGrey};
    }
    &::after {
      border: transparent;
      background-color: ${theme.color.lighterGrey};
    }

    ${hover(`
    transform: translate(-50%, -100%);

    &::after, svg {
      transform: translate(-50%, -50%);
    }
  `)}
  `}

  ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 2 &&
    `
    width: 1rem;
    height: 1rem;

    @media ${respondTo(MediaQuery.Large)} {
    width: 2rem;
    height: 2rem;
    }
  `}

    ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 3 &&
    `
    width: 1.125rem;
    height: 1.125rem;

    @media ${respondTo(MediaQuery.Large)} {
    width: 2.3125rem;
    height: 2.3125rem;
    }
  `}

    ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 4 &&
    `
    width: 1.25rem;
    height: 1.25rem;
     }

    @media ${respondTo(MediaQuery.Large)} {
    width: 2.625rem;
    height: 2.625rem;
    }
  `}

    ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 5 &&
    `
    width: 1.375rem;
    height: 1.375rem;

    @media ${respondTo(MediaQuery.Large)} {
    width: 2.9375rem;
    height: 2.9375rem;
    }
  `}

   ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 6 &&
    `
    width: 1.5rem;
    height: 1.5rem;

    @media ${respondTo(MediaQuery.Large)} {
    width: 3.25rem;
    height: 3.25rem;
    }
  `}

    ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 7 &&
    `
    width: 1.625rem;
    height: 1.625rem;

    @media ${respondTo(MediaQuery.Large)} {
    width: 3.5625rem;
    height: 3.5625rem;
    }
  `}

    ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 8 &&
    `
    width: 1.75rem;
    height: 1.75rem;

    @media ${respondTo(MediaQuery.Large)} {
    width: 3.875rem;
    height: 3.875rem;
    }
  `}

    ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 9 &&
    `
    width: 1.875rem;
    height: 1.875rem;
  `}

    ${({ $isCluster, $pointCount }) =>
    $isCluster &&
    $pointCount === 10 &&
    `
    width: 2rem;
    height: 2rem;
  `}

  ${({ $isCluster }) =>
    !$isCluster &&
    `
  @media ${respondTo(MediaQuery.Medium)} {
    height: 2.5rem;
    padding: 0 1.25rem;

    &::before {
      ${size('0.625rem')};
      bottom: -0.1875rem;
      }
  `}
`;

export const StyledClusterNumber = styled.div<{
  $isNumber?: boolean;
  $isConnected?: boolean;
  $isUnserved?: boolean;
  $isNonstop?: boolean;
  $nonStopOnly?: boolean;
}>`
  font-size: 14px;
  font-weight: ${FontWeight.Bold};
  color: ${({ theme }) => theme.color.white};

  span {
    ${typeStyle.normal};
    position: absolute;
    top: -0.5313rem;
    margin-left: 0.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.875rem;
    padding: 1.2188rem;
    background-color: ${({ theme }) => theme.color.white};
    color: ${({ theme }) => theme.color.black};
    transform: translate(-50%, -100%);
    white-space: nowrap;
    box-shadow: 0 0.25rem 1.25rem rgba(48, 76, 178, 0.2);

    cursor: pointer;
    transition: transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};

    &::before {
      ${pseudo()};
      ${size('0.4375rem')};
      bottom: -0.1875rem;
      border-radius: ${({ theme }) => theme.defaultCssProps.borderRadius.xSmall};
      background-color: ${({ theme }) => theme.color.white};
      transform: rotate(45deg);
    }
  }

  @media ${respondTo(MediaQuery.Large)} {
    font-size: ${FontSize.Large};
  }

  ${({ $isNumber }) =>
    !$isNumber &&
    `
   margin-right: 0.75rem;
  `}

  ${({ $isConnected, theme }) =>
    $isConnected &&
    `
   color: ${theme.color.blue};
  `}
  ${({ $isUnserved, $isConnected, $isNonstop, theme }) =>
    $isUnserved &&
    !$isConnected &&
    !$isNonstop &&
    `
    cursor: not-allowed;
    color: ${theme.color.blue};

    &::before {
      cursor: not-allowed;
      border: transparent;
      background-color: ${theme.color.lighterGrey};
  }
  `}
  ${({ $isNonstop, $nonStopOnly, theme }) =>
    $nonStopOnly &&
    !$isNonstop &&
    `
    cursor: not-allowed;
    color: ${theme.color.blue};

    &::before {
      cursor: not-allowed;
      border: transparent;
      background-color: ${theme.color.lighterGrey};
  }
  `}
`;

export const StyledLocationDot = styled.div<{
  $unavailable?: boolean;
  $isSuspended: boolean;
  $isInSeasonalService: boolean;
  $isUnserved?: boolean;
  $isConnected?: boolean;
}>`
  ${size('0.625rem')};
  position: relative;
  cursor: pointer;
  border-radius: 50%;
  background-color: ${({ theme, $unavailable }) =>
    $unavailable ? theme.color.grey : theme.color.blue};
  transform: translate(-50%, -50%);
  transition: transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};

  &::after {
    ${pseudo()};
    ${size('1.6875rem')};
    cursor: pointer;
    right: -80%;
    bottom: -80%;
    border-radius: 50%;
    border-style: solid;
    border-color: transparent;
  }

  @media ${respondTo(MediaQuery.Medium)} {
    &::after {
      visibility: hidden;
    }
  }

  ${({ $unavailable }) =>
    $unavailable &&
    `
      cursor: not-allowed;
  `}
  ${({ $isSuspended, theme }) =>
    $isSuspended &&
    `
    cursor: not-allowed;
    height: 0.5625rem;
    width: 0.5625rem;
    transform: translate(-50%, -50%) rotate(45deg);
    border-radius: ${theme.defaultCssProps.borderRadius.xSmall};
    background-color: ${theme.color.orange};

      &::after {
    ${pseudo()};
    ${size('1.6875rem')};
    cursor: pointer;
    left: -60%;
    bottom: -60%;
    border-radius: 50%;
    border-style: solid;
    border-color: transparent;
  }

  @media ${respondTo(MediaQuery.Medium)} {
    &::after {
      visibility: hidden;
    }
  }
  `}

  ${({ $isInSeasonalService, theme }) =>
    $isInSeasonalService &&
    `
    background-color: transparent;
    cursor: not-allowed;

    svg {
      height: 1.25rem;
      width: 1.25rem;
      color: ${theme.color.darkerGrey};
      position: absolute;
      bottom: 0;
      left: 0;
      transform: translate(-50%, -50%);
    }

         &::after {
    ${pseudo()};
    ${size('1.6875rem')};
    cursor: pointer;
    left: -60%;
    bottom: -60%;
    border-radius: 50%;
    border-style: solid;
    border-color: transparent;
  }

  @media ${respondTo(MediaQuery.Medium)} {
    &::after {
      visibility: hidden;
    }
  }
  `}

  ${({ $isSuspended, $isInSeasonalService }) =>
    !$isSuspended &&
    !$isInSeasonalService &&
    `
    ${hover(`
      transform: translate(-50%, -50%) scale(1.1);
    `)}
  `}

   ${({ $isConnected, theme }) =>
    $isConnected &&
    `
      border: 2px solid ${theme.color.blue};
      background-color: transparent;

      &::after {
      ${pseudo()};
      ${size('1.6875rem')};
      cursor: pointer;
      right: -160%;
      bottom: -160%;
      border-radius: 50%;
      border-style: solid;
      border-color: transparent;
  }

  @media ${respondTo(MediaQuery.Medium)} {
    &::after {
      visibility: hidden;
    }
  }
  `}

  ${({ $isUnserved, $isConnected, theme }) =>
    $isUnserved &&
    $isConnected &&
    `
    cursor: not-allowed;
    border: transparent;
    background-color: ${theme.color.lighterGrey};

               &::after {
    ${pseudo()};
    ${size('1.6875rem')};
    cursor: pointer;
    left: -80%;
    top: -80%;
    border-radius: 50%;
    border-style: solid;
    border-color: transparent;
  }

  @media ${respondTo(MediaQuery.Medium)} {
    &::after {
      visibility: hidden;
    }
  }


  `}
`;

export const StyledNavigationControl = styled(NavigationControl)`
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: ${zIndex('map-overlay')};

  button.mapboxgl-ctrl-icon {
    ${size('2.5rem')};
    display: flex;
    justify-content: center;
    align-items: center;
    transition: transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};

    span.mapboxgl-ctrl-icon {
      ${size('2px', '0.75rem')};
      position: relative;
      background-color: ${({ theme }) => theme.color.white};
      background-image: none;
    }

    &.mapboxgl-ctrl-zoom-in {
      span::after {
        ${pseudo()};
        ${size('100%')};
        transform: rotate(90deg);
        background-color: ${({ theme }) => theme.color.white};
      }
    }

    &:last-child {
      border-top: 0;
    }

    ${hover(`
      transform: scale(1.1);
    `)}

    &:focus {
      transform: scale(1.06);
      border: 1px solid ${({ theme }) => theme.color.blue};
      z-index: 1;
      box-shadow: 0px 0px 4px ${({ theme }) => theme.color.blue};
    }
  }

  .mapboxgl-ctrl-group {
    width: 2.5rem;
    height: 5.0625rem;
    background-color: ${({ theme }) => theme.color.lightBlack};
    border-radius: ${({ theme }) => theme.defaultCssProps.borderRadius.small};

    &::after {
      ${pseudo()};
      ${size('1px', '100%')};
      top: 2.5rem;
      background-color: ${({ theme }) => rgba(theme.color.lightGrey, 0.3)};
    }

    &:not(:empty) {
      box-shadow: 0 0 1.25rem rgba(48, 76, 178, 0.05);
    }
  }
`;

export const StyledGlobeButton = styled(AbstractButton)`
  ${size('2.5rem')};
  position: absolute;
  bottom: 0.625rem;
  right: 5.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.defaultCssProps.borderRadius.small};
  color: ${({ theme }) => theme.color.white};
  background-color: ${({ theme }) => theme.color.lightBlack};
  z-index: ${zIndex('active-marker')};
  cursor: pointer;
  transition: box-shadow ${TransitionDuration.Slow} ${Ease.EaseOut},
    transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};

  svg {
    ${size('1rem')};
  }

  ${hover(`
      transform: scale(1.03);
      box-shadow: 0 0.25rem 1.25rem rgba(48, 76, 178, 0.2);
  `)}

  &:focus {
    transform: scale(1.06);
    border: 1px solid ${({ theme }) => theme.color.blue};
    box-shadow: 0px 0px 4px ${({ theme }) => theme.color.blue};
  }

  @media ${respondTo(MediaQuery.Medium)} {
    bottom: 1.25rem;
    right: 4.375rem;
    z-index: ${zIndex('map-overlay')};
  }
`;

// fareComponent
export const StyleLocationContainerWithFare = styled.div<StyledAirportNameProps>`
  ${typeStyle.normal};
  position: absolute;
  top: -1.4063rem;
  // display: flex;
  align-items: center;
  justify-content: center;
  height: 1.875rem;
  background-color: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.black};
  transform: translate(-50%, -100%);
  white-space: nowrap;
  box-shadow: 0 0.25rem 1.25rem rgba(48, 76, 178, 0.2);

  cursor: pointer;
  transition: transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};

  #routeFare {
    padding: 0.625rem 1.25rem;
    display: table;
    min-height: 52px;
    // top: -3.4375rem !important;
    padding: 0.3125rem 1.25rem;
    min-height: 42px;
    font-family: 'SouthwestSans-Regular';
  }
  #routeFare,
  button {
    position: relative;
    width: 100%;
    text-align: center;
    transform: none;
    top: -2.9688rem;
  }
  button {
    ::after {
      // display: none;
    }

    &:hover {
      transform: none;
    }
  }

  span {
    margin-left: 0.625rem;
  }

  &::before {
    ${pseudo()};
    ${size('0.4375rem')};
    bottom: -0.1875rem;
    border-radius: ${({ theme }) => theme.defaultCssProps.borderRadius.xSmall};
    background-color: ${({ theme }) => theme.color.white};
    transform: rotate(45deg);
  }

  // &::after {
  //   ${pseudo()};
  //   ${size('0.625rem')};
  //   bottom: -1.5625rem;
  //   left: 50%;
  //   border-radius: 50%;
  //   background-color: ${({ theme }) => theme.color.blue};
  //   transform: translate(-50%, -50%);
  //   transition: transform ${TransitionDuration.Slow} ${Ease.EaseOutBack};
  // }

  &:hover {
    transform: translate(-50%, calc(-100% + -0.3125rem));

    button::after,
    svg {
      transform: translate(-50%, calc(-50% + 0.3125rem)) scale(1.1);
    }
  }

  &.marker-is-disabled {
    cursor: not-allowed;
    color: ${({ theme }) => theme.color.grey};

    &::after {
      border: transparent;
      background-color: ${({ theme, $isSuspended }) =>
        $isSuspended ? theme.color.orange : theme.color.lighterGrey};
    }

    ${hover(`
    transform: translate(-50%, -100%);

    &::after, svg {
      transform: translate(-50%, -50%);
    }
    `)}
  }
`;
